import { IImgUrlOptions } from './types';

const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

const optionSize = (size: number, minWidth?: number | null) => {
  if (minWidth) {
    return `(min-width: ${minWidth}px) ${size}w`;
  }
  return `${size}w`;
};

const optionSrcSet = (
  imgUrl: (src: string, options: IImgUrlOptions) => string,
  src: string,
  size: number,
  imgOptions?: Omit<IImgUrlOptions, 'width'>
) => {
  return `${imgUrl(src, { width: size, ...imgOptions })} ${size}w`;
};

export const generateTwSizes = (maxWidth: keyof typeof breakpoints) => {
  const sortedBr = Object.entries(breakpoints).sort((a, b) => a[1] - b[1]) as Array<
    [keyof typeof breakpoints, number]
  >;

  return Object.fromEntries(
    sortedBr
      .slice(0, -1)
      .map(([breakpoint, breakpointValue], index) => {
        if (breakpointValue < breakpoints[maxWidth]) {
          const nextBreakpointValue = breakpoints[sortedBr[index + 1][0]];
          return [breakpoint, nextBreakpointValue];
        }
        return null;
      })
      .filter(Boolean) as Array<[string, number]>
  ) as Partial<Record<keyof typeof breakpoints, number>>;
};

/** @deprecated Use `Picture` from `@boc/images` instead, which uses the ImageProvider context. */
export const tailwindMaxWidthSizes = (
  imgUrl: (src: string, options: IImgUrlOptions) => string,
  src: string,
  options?: {
    maxWidth?: keyof typeof breakpoints;
    imgOptions?: Omit<IImgUrlOptions, 'width'>;
  }
) => {
  const { maxWidth, imgOptions } = options || {};

  if (!maxWidth || maxWidth === 'sm') {
    return tailwindSizes(imgUrl, src, breakpoints['sm'], { imgOptions: options?.imgOptions });
  }

  const sizes = generateTwSizes(maxWidth);

  return tailwindSizes(imgUrl, src, breakpoints.sm, { sizes, imgOptions });
};

/** @deprecated Use `Picture` from `@boc/images` instead, which uses the ImageProvider context. */
export const tailwindSizes = (
  imgUrl: (src: string, options: IImgUrlOptions) => string,
  src: string,
  defaultSize: number,
  options: {
    sizes?: Partial<Record<keyof typeof breakpoints, number>>;
    imgOptions?: Omit<IImgUrlOptions, 'width'>;
  }
) => {
  const srcSetOptions = Object.values(options.sizes || {}).reduce((acc, size) => {
    if (size) {
      acc.push(optionSrcSet(imgUrl, src, size, options.imgOptions));
    }
    return acc;
  }, [] as string[]);

  const sizesOptions = (
    Object.entries(options.sizes || {}) as Array<[keyof typeof breakpoints, number]>
  ).reduce((acc, [key, size]) => {
    if (size) {
      acc.push(optionSize(size, breakpoints[key]));
    }
    return acc;
  }, [] as string[]);

  return {
    src: imgUrl(src, { width: defaultSize, ...options.imgOptions }),
    ...(srcSetOptions.length > 0
      ? {
          srcSet: [
            `${imgUrl(src, { width: defaultSize, ...options.imgOptions })} ${defaultSize}w`,
            ...srcSetOptions,
          ].join(', '),
        }
      : {}),
    ...(sizesOptions.length > 0 ? { sizes: [...sizesOptions, `${defaultSize}w`].join(', ') } : {}),
  };
};

/** @deprecated Use `Picture` from `@boc/images` instead, which uses the ImageProvider context. */
export const TwPicture = ({
  imgUrl,
  src,
  defaultSize,
  twSizes,
  imgOptions,
  dprVariants = [1.2, 1.5, 1.8, 2, 2.5, 3],
  ...props
}: {
  imgUrl: (src: string, options: IImgUrlOptions) => string;
  src: string;
  defaultSize: number;
  twSizes?: Partial<Record<keyof typeof breakpoints, number>>;
  imgOptions?: Omit<IImgUrlOptions, 'width'>;
  dprVariants?: number[];
} & React.ImgHTMLAttributes<HTMLImageElement>) => {
  return (
    <picture>
      {twSizes &&
        (Object.entries(twSizes || {}) as Array<[keyof typeof breakpoints, number]>)
          .sort((a, b) => breakpoints[b[0]] - breakpoints[a[0]])
          .map(([key, size]) => (
            <source
              key={key}
              media={`(min-width: ${breakpoints[key]}px)`}
              srcSet={[
                imgUrl(src, { width: size, ...imgOptions }),
                dprVariants?.map(
                  (x) => `${imgUrl(src, { width: Math.floor(size * x), ...imgOptions })} ${x}x`
                ),
              ].join(', ')}
            />
          ))}
      <img
        src={imgUrl(src, { width: defaultSize, ...imgOptions })}
        srcSet={
          dprVariants
            ? [
                imgUrl(src, { width: defaultSize, ...imgOptions }),
                dprVariants?.map(
                  (x) =>
                    `${imgUrl(src, { width: Math.floor(defaultSize * x), ...imgOptions })} ${x}x`
                ),
              ].join(', ')
            : undefined
        }
        {...props}
      />
    </picture>
  );
};

/** @deprecated Use `Picture` from `@boc/images` instead, which uses the ImageProvider context. */
export const TwMaxWidthPicture = ({
  imgUrl,
  src,
  maxWidth,
  imgOptions,
  dprVariants,
  ...props
}: {
  imgUrl: (src: string, options: IImgUrlOptions) => string;
  src: string;
  maxWidth?: keyof typeof breakpoints;
  imgOptions?: Omit<IImgUrlOptions, 'width'>;
  dprVariants?: number[];
} & React.ImgHTMLAttributes<HTMLImageElement>) => {
  if (!maxWidth || maxWidth === 'sm') {
    return (
      <TwPicture
        imgUrl={imgUrl}
        src={src}
        defaultSize={breakpoints.sm}
        dprVariants={dprVariants}
        imgOptions={imgOptions}
        {...props}
      />
    );
  }

  const sizes = generateTwSizes(maxWidth);

  return (
    <TwPicture
      imgUrl={imgUrl}
      src={src}
      twSizes={sizes}
      defaultSize={breakpoints.sm}
      dprVariants={dprVariants}
      imgOptions={imgOptions}
      {...props}
    />
  );
};
